/**
 * Logistics, Warehousing & 3PL — 9 documents.
 *
 * A 3PL fee schedule is where CPG margin quietly disappears. The per-case price you negotiated is
 * one line on a rate card that also charges you to receive, store, pick, pack, ship, count, report
 * and eventually release your own inventory. Model the real cost of a real order before signing.
 */
export const LOGISTICS_DOCS = [
  {
    id: "three-pl-agreement",
    name: "3PL Agreement",
    category: "logistics",
    subcategory: "Core 3PL",
    aliases: [
      "Third-Party Logistics Agreement",
      "3PL Services Agreement",
      "Logistics Provider Agreement",
      "Warehousing and Fulfillment Services Agreement",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Third-Party Logistics Provider (3PL)",
    whenYouNeedIt:
      "Before your first pallet leaves the co-packer for someone else's warehouse. Also the moment you outgrow your garage or your co-packer's floor space and someone offers to 'just hold it for you' on a rate card and an email.",
    whatItDoes:
      "Puts your finished goods into a third party's building and their hands: they receive it, store it, count it, pick it, pack it and ship it. The agreement sets what each of those movements costs, what standard of care applies, who is accountable when the count is wrong, and what it takes to get your inventory back out.",
    whyItMatters:
      "This is the agreement where CPG margin quietly disappears, and it disappears one small fee at a time. Founders negotiate the per-order rate, sign, and discover in month three that receiving, storage, pallet handling, per-case picking, EDI transactions and a minimum monthly charge together cost more than the headline rate they argued about. The number that matters is the fully loaded cost of a real order at your real basket size, computed before you sign. Second only to that: this is the building that physically holds everything your business owns, and the terms decide whether you can get it back.",
    majorTerms: [
      "Storage fees and the unit they are charged on (pallet, bin, shelf, cubic foot)",
      "Pallet handling and in/out movement fees",
      "Inbound receiving fees per container, pallet, carton or unit",
      "Outbound fees per order, per shipment and per pallet",
      "Pick fees per unit and per additional line",
      "Case-handling and full-case pick fees",
      "Per-order and account minimum monthly charge",
      "EDI setup and per-transaction fees",
      "Special project, relabel and rework hourly rates",
      "Inventory accuracy standard and how it is measured",
      "Shrinkage allowance and who absorbs loss above it",
      "Damaged goods responsibility and claim process",
      "Insurance limits and liability cap basis",
      "Term, renewal, notice and termination",
      "Inventory removal on termination: cost, timeline and conditions",
    ],
    clausesToWatch: [
      {
        clause: "Fee schedule amendment rights",
        why: "A right to revise the rate card on short notice means every number you modelled is provisional, and your unit economics move without your agreement.",
      },
      {
        clause: "Minimum monthly charge",
        why: "A minimum set for the volume you promised rather than the volume you have turns a slow launch quarter into a fixed cost you pay for storage and picking you never used.",
      },
      {
        clause: "Inventory accuracy standard",
        why: "An accuracy commitment written as a percentage means nothing until the contract says what it is a percentage of — units, cases, pallets or SKUs each produce a completely different tolerance for the same words.",
      },
      {
        clause: "Shrinkage allowance",
        why: "If the agreement grants an allowance before any liability attaches, everything lost inside that band is your loss and the 3PL never has to explain it.",
      },
      {
        clause: "Limitation of liability basis",
        why: "Liability limited by weight or by a per-pallet figure rather than by the value of the goods can leave you recovering a fraction of the cost of the product that went missing.",
      },
      {
        clause: "Warehouse lien and right to hold goods",
        why: "A broad right to hold your inventory against any amount they say is owed means a billing dispute becomes a stock-out, and the leverage sits entirely on their side.",
      },
      {
        clause: "Termination and inventory removal",
        why: "Without a stated removal cost, timeline and cooperation obligation, leaving a 3PL can cost more than a year of storage and take longer than your next production run.",
      },
      {
        clause: "Storage conditions and temperature obligations",
        why: "If the building's temperature and humidity commitments are not written down, product quality failures land on your label with no one to bill.",
      },
      {
        clause: "Service levels without a remedy",
        why: "A stated same-day ship cutoff with no credit, cure right or termination trigger attached is a marketing claim, not an obligation.",
      },
    ],
    cpgIssues: [
      "Food, beverage and supplement inventory needs lot-level traceability and FEFO rotation. A 3PL built for apparel can pick your product perfectly and still be unable to tell you which retailer received a specific lot — which is exactly the question a recall drill asks.",
      "Expiration and code dates have to be captured at receiving and used to drive picking, or you will ship short-dated product into a retailer that rejects it at the dock and charges you for the privilege.",
      "Functional beverages, probiotics, gummies and chocolate need temperature-controlled or at minimum climate-managed storage; an ambient warehouse in August is a product-quality decision you are making by default.",
      "Beverage in cans and glass generates leakers, crushed cases and pallet damage at a rate no software company would recognise — the damage clause and the claims process matter more here than the pick rate.",
      "Case-pick and eaches-pick are different operations with different costs; a rate card that quotes only one of them will surprise you the first time a retailer orders full cases and a consumer orders one unit.",
      "Retailer routing guides, pallet configuration, label placement and appointment scheduling all sit with the 3PL, and non-compliance comes back as a retailer deduction charged to you, not to them.",
      "Mock recall and traceability response times should be a contractual obligation with a stated turnaround, because you will be asked to produce that record under time pressure and with your retail relationship on the line.",
    ],
    founderMistakes: [
      "Negotiating the per-order pick rate hard, signing, and finding that receiving, storage, per-case handling and the minimum monthly had turned a profitable direct-to-consumer order into a break-even one.",
      "Never modelling the fee schedule against actual basket size, so a business built on single-unit orders was paying an order-level fee on nearly every unit sold.",
      "Accepting an inventory accuracy commitment without asking what it was measured against, then being told a cycle count that was short by hundreds of units sat inside the agreed tolerance.",
      "Choosing a low-cost 3PL with no lot-tracking capability, then being unable to answer a retailer's traceability request during a quality incident.",
      "Disputing an invoice and discovering the agreement let the 3PL hold every pallet until it was paid, days before a chain reset.",
      "Terminating without reading the removal clause, then paying per-pallet exit handling, loading and rework charges that exceeded the cost of the inventory being moved.",
    ],
    negotiationPoints: [
      "Ask for the complete fee schedule as a dated exhibit, fixed for a stated period, with written notice and a termination right if it changes.",
      "Define the inventory accuracy standard explicitly: what is counted, on what cycle, measured against what base, and what happens when a count comes up short.",
      "Set a shrinkage allowance you have actually modelled, and make loss above it their liability with a documented claim process and a stated response time.",
      "Push the liability cap off a weight or per-pallet basis and onto the value of the goods, and ask to be named as an additional insured.",
      "Require lot code and expiration capture at receiving, FEFO picking as the default, and a stated traceability response time.",
      "Narrow the lien to undisputed, invoiced amounts, and carve out inventory needed to fill a purchase order already accepted.",
      "Agree the exit terms in advance: notice period, removal charges, loading cooperation, and a deadline for final reconciliation.",
      "Run your last complete month of real orders through their rate card before signing, and put that model in the file.",
    ],
    redFlags: [
      "A right to change rates at any time on short notice, with no corresponding termination right for you.",
      "A lien over all inventory for any amount claimed, including amounts you have disputed in writing.",
      "Liability capped by weight or per pallet with no option to declare value or insure the goods.",
      "No stated inventory accuracy standard, and no defined shrinkage allowance.",
      "No lot code or expiration date capture for a food, beverage or supplement product.",
      "Silence on what it costs and how long it takes to remove your inventory on termination.",
      "A minimum monthly charge with no ramp period and no relief if their service fails.",
      "Storage conditions described only as 'suitable' or 'industry standard' with no temperature commitment.",
    ],
    beforeYouSign: [
      "The complete rate card, including every accessorial, project rate and pass-through not shown on the first page.",
      "Your own order profile — average units per order, case versus eaches mix, seasonality — run through their fees line by line.",
      "A demonstration of their lot tracking and a mock traceability request answered in your presence.",
      "Certificates of insurance, the liability cap basis, and confirmation of whether you can declare higher value.",
      "A written exit plan: notice, removal charges, timeline and who loads the trucks.",
    ],
    related: [
      "warehouse-agreement",
      "fulfillment-agreement",
      "ecommerce-fulfillment-agreement",
      "pick-and-pack-agreement",
      "transportation-agreement",
      "recall-responsibility-agreement",
    ],
    attorneyReview: "yes",
    templateId: "three-pl-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "warehouse-agreement",
    name: "Warehouse Agreement",
    category: "logistics",
    subcategory: "Storage and inventory",
    aliases: [
      "Public Warehouse Agreement",
      "Storage Agreement",
      "Warehousing Services Agreement",
      "Bailment Agreement",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Warehouse Operator",
    whenYouNeedIt:
      "When you need somewhere to put pallets and nothing else — no picking, no packing, no shipping to consumers. Common after a large production run, ahead of a retail launch, or when your 3PL charges more for bulk storage than a dedicated warehouse does.",
    whatItDoes:
      "Places your goods in someone else's building under a duty of care. It sets the storage rate and the unit it is charged on, the handling charges for moving product in and out, the conditions the building will hold, how inventory is counted and reported, and what happens when the count disagrees with your records.",
    whyItMatters:
      "Storage looks like the simplest thing you will ever buy and produces the most argument. Two costs decide whether it works: the storage rate itself, and the in-and-out handling charges that are invisible until you move product. Then there is the harder question — when a cycle count comes up short, who is accountable, and what does the shrinkage allowance actually let them lose before anyone owes you anything. Founders discover both answers during a stock-out, which is the worst possible time to be reading the clause.",
    majorTerms: [
      "Storage rate and unit: per pallet, per pallet position, per square or cubic foot",
      "Billing cycle and whether storage is charged monthly, per half-month or per day",
      "Inbound handling and receiving charges",
      "Outbound handling, staging and loading charges",
      "Pallet in and pallet out fees, and pallet exchange or pallet purchase",
      "Temperature, humidity and storage condition commitments",
      "Lot code, production date and expiration capture at receiving",
      "Rotation standard: FIFO or FEFO",
      "Cycle count frequency, inventory reporting and reconciliation process",
      "Inventory accuracy standard and shrinkage allowance",
      "Damaged goods responsibility and claim procedure",
      "Insurance, liability limits and declared value",
      "Warehouse lien over stored goods",
      "Term, termination and inventory removal",
    ],
    clausesToWatch: [
      {
        clause: "Storage billing period",
        why: "Storage billed per whole month or half-month rather than per day means product that arrives on the last day of a period can be charged a full period twice over.",
      },
      {
        clause: "Rotation standard",
        why: "If the agreement says nothing, the warehouse will rotate to suit its racking, and dated product will age at the back of a bay until it is worth nothing.",
      },
      {
        clause: "Inventory accuracy and reconciliation",
        why: "Without a stated count cycle, a defined accuracy base and a written reconciliation process, a shortfall becomes an argument between two spreadsheets with no tiebreaker.",
      },
      {
        clause: "Shrinkage allowance",
        why: "An allowance expressed as a flat percentage of throughput can quietly permit a meaningful number of cases to vanish each year with no explanation and no credit.",
      },
      {
        clause: "Temperature and condition obligations",
        why: "Language promising a 'dry, clean facility' commits to nothing measurable, and heat damage to chocolate, gummies or probiotics is not usually visible at pickup.",
      },
      {
        clause: "Warehouse lien",
        why: "A lien for unpaid charges is common in storage contracts, but a broad one drafted to cover disputed and unrelated amounts turns a billing disagreement into a hostage situation.",
      },
      {
        clause: "Claim notice periods",
        why: "A short window to notify loss or damage, running from delivery rather than from discovery, can extinguish a claim before your first cycle count finds the problem.",
      },
      {
        clause: "Removal on termination",
        why: "Exit handling charges, minimum notice and a requirement to settle all charges before release can make leaving cost more than staying another quarter.",
      },
    ],
    cpgIssues: [
      "Expiration and code dates must be recorded at receiving and drive picking order — for food, beverage and supplements the right rotation standard is usually FEFO, not FIFO, because the oldest pallet and the shortest-dated pallet are not always the same one.",
      "Lot-level records are the backbone of a withdrawal or recall; the warehouse holds them, so the obligation to keep them and produce them on demand belongs in this contract.",
      "Temperature-controlled or climate-managed space matters for functional beverages, probiotics, gummies, chocolate and oil-based supplements — specify a range and who monitors it, not just 'ambient'.",
      "Freeze risk is a real exposure for canned and bottled beverage in cold-climate warehouses and on unheated docks; a burst can is a full-pallet loss.",
      "Pallet configuration, tie-high and stacking limits protect the product — an agreement that lets the warehouse double-stack your pallets to save space can crush a whole layer of cases.",
      "Minimum remaining shelf life at outbound matters as much as at inbound: retailers reject short-dated product at the dock, and the rejection cost lands on you.",
      "Damaged-goods disposal for food and supplements needs a documented process, because product that leaves a warehouse as 'damaged' and reappears for sale is a brand problem you cannot undo.",
    ],
    founderMistakes: [
      "Comparing warehouses on the pallet storage rate alone, then paying more in in-and-out handling charges than in storage across the first year.",
      "Never asking about rotation, then finding a pallet of the original production run still racked behind newer stock, months past the date a retailer would accept.",
      "Accepting a shrinkage allowance without converting it into cases, and only realising later that the tolerance covered several thousand dollars of product a year.",
      "Storing canned beverage through a winter in an unheated building and losing an entire run to freeze damage that nobody had agreed to be responsible for.",
      "Missing a short claim-notice window because the shortage was discovered at the next quarterly count rather than on the day of delivery.",
      "Assuming the warehouse's insurance covered the value of the stored goods, and learning at claim time that it covered their operations rather than your inventory.",
    ],
    negotiationPoints: [
      "Ask for storage billed on a daily or prorated basis rather than per whole period, and confirm exactly when a new period starts.",
      "Write in FEFO rotation, capture of lot code and expiration at receiving, and a stated minimum remaining shelf life at outbound.",
      "Set a cycle count cadence, an inventory report you actually receive, and a written reconciliation procedure with a stated resolution timeline.",
      "Convert the shrinkage allowance into cases and units before agreeing to it, and make loss above the allowance a documented credit rather than a negotiation.",
      "Specify a temperature and humidity range with monitoring and a notification obligation when it is breached.",
      "Narrow the lien to undisputed invoiced storage and handling charges for the goods actually stored.",
      "Ask whether you can declare value or arrange your own inventory coverage, and get the liability basis in writing.",
      "Agree removal terms up front: notice, exit handling rate, loading cooperation and a deadline for final reconciliation.",
    ],
    redFlags: [
      "No stated rotation standard for dated product.",
      "No lot code or expiration capture at receiving.",
      "A shrinkage allowance with no cap, or one expressed so that no shortfall ever becomes their responsibility.",
      "A lien covering any amount owed on any account, including disputed charges.",
      "Storage conditions described only as 'suitable for the goods'.",
      "A claim window running from delivery, measured in days, with no discovery trigger.",
      "A right to move, consolidate or double-stack your pallets without notice or restriction.",
      "Release of your inventory conditioned on payment of all charges including those under dispute.",
    ],
    beforeYouSign: [
      "A walk of the actual building where your pallets will sit, including the dock and any unheated staging area.",
      "The full charge list: storage, receiving, outbound, pallet in, pallet out, staging, relabel and exit handling.",
      "Their inventory report format, count cadence and a sample reconciliation.",
      "Written confirmation of the temperature range, monitoring method and who is notified when it moves.",
      "Confirmation of what their insurance covers and whether your stored goods are covered at value.",
    ],
    related: [
      "three-pl-agreement",
      "fulfillment-agreement",
      "logistics-services-agreement",
      "transportation-agreement",
      "food-safety-agreement",
      "consignment-agreement",
    ],
    attorneyReview: "yes",
    templateId: "warehouse-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "fulfillment-agreement",
    name: "Fulfillment Agreement",
    category: "logistics",
    subcategory: "Fulfillment operations",
    aliases: [
      "Order Fulfillment Agreement",
      "Fulfillment Services Agreement",
      "Pick, Pack and Ship Agreement",
    ],
    stages: ["launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Fulfillment Provider",
    whenYouNeedIt:
      "When someone else starts turning your orders into parcels and pallets — your own website orders, wholesale purchase orders, subscription boxes, or all three running through the same building.",
    whatItDoes:
      "Commits a provider to receive your orders, pick the right units, pack them to your standard and hand them to a carrier within an agreed time. It prices each of those steps and sets the accuracy, timing and reporting you are entitled to expect.",
    whyItMatters:
      "Fulfillment is priced in fractions of a dollar and lost in fractions of a dollar. An order fee, a first-pick fee, an additional-pick fee, a carton charge, packing materials, an insert charge and a peak surcharge each look trivial and together decide whether a low-value order makes money at all. The second exposure is accuracy: a mis-pick costs you the unit, the replacement shipment, the return, the refund and usually the customer, and none of that is recovered unless the agreement says who pays for errors.",
    majorTerms: [
      "Per-order base fee and what it includes",
      "First pick and additional pick fees per unit or per line",
      "Case pick and full-case handling rates",
      "Packing materials, carton, dunnage and insert charges",
      "Kitting, bundling and multi-pack assembly rates",
      "Order cutoff time and ship-by service level",
      "Order accuracy standard and responsibility for mis-picks",
      "Returns receiving, inspection and restocking fees",
      "Peak season surcharges and capacity commitments",
      "Carrier selection, rate pass-through and any markup",
      "Systems integration, order feed and reporting",
      "Minimum monthly volume or minimum monthly charge",
      "Term, termination and transition of open orders",
    ],
    clausesToWatch: [
      {
        clause: "What the base order fee actually covers",
        why: "If picking, packaging, inserts and carton are all separate line items, the quoted order fee is a fraction of what shipping an order really costs you.",
      },
      {
        clause: "Ship-by service level and remedy",
        why: "A same-day cutoff with no credit or termination trigger attached leaves you explaining someone else's delays to your own customers with no recourse.",
      },
      {
        clause: "Responsibility for mis-picks and mis-ships",
        why: "Without a clause putting the cost of their error on them, you pay twice for every wrong unit and absorb the refund on top.",
      },
      {
        clause: "Carrier rate pass-through",
        why: "A provider billing you their published rate while buying at a negotiated one turns your shipping cost into their margin, invisibly.",
      },
      {
        clause: "Peak surcharges",
        why: "Surcharges applied in your highest-volume months, announced rather than agreed, hit exactly the revenue you were counting on to fund the year.",
      },
      {
        clause: "Returns handling and restocking",
        why: "If returns are priced per unit with inspection and refurbishment billed hourly, a high-return promotion can cost more to unwind than it earned.",
      },
      {
        clause: "Systems integration and order data",
        why: "Integration built and owned by the provider makes leaving expensive, and order-level data you cannot export leaves you unable to audit their invoices.",
      },
    ],
    cpgIssues: [
      "Pick accuracy across flavour and SKU variants is the recurring failure mode in CPG — six flavours in near-identical cans are a picking problem no apparel operation ever has to solve.",
      "FEFO picking has to apply to consumer orders too, not just wholesale pallets, or short-dated units will be the ones that reach the customers most likely to post about it.",
      "Beverage in glass or cans needs a defined pack-out standard — dividers, dunnage, carton grade — because a leaker in transit destroys the parcel, the other units and the review.",
      "Temperature-sensitive product shipped in summer may need insulated packaging or a cold pack; decide who specifies it, who pays and what happens when it is skipped.",
      "Kitting for club packs, retail-ready displays, variety packs and gift sets is manual labour priced separately, and it is the single most under-modelled line on the rate card.",
      "Lot codes must travel with the outbound order record, or a withdrawal cannot reach the individual consumers who received a specific production run.",
      "Marketplace and retailer prep requirements — labels, polybags, carton markings, case quantities — are their own charge and their own failure mode when a shipment is rejected at receiving.",
    ],
    founderMistakes: [
      "Comparing providers on the per-order fee alone and missing that one of them charged separately for the box, the dunnage and the insert on every single order.",
      "Launching a sampler pack without pricing the kitting labour, then discovering the assembly cost more than the margin on the pack.",
      "Accepting the provider's carrier account without asking what the underlying negotiated rate was, and paying a markup on every parcel for two years.",
      "Signing with no accuracy standard, then absorbing the cost of replacement shipments for months of mis-picked flavours.",
      "Running a promotion that tripled volume in a week with no capacity commitment in the contract, and watching orders ship a week late through the busiest period of the year.",
    ],
    negotiationPoints: [
      "Get a single fully loaded cost per representative order, in writing, including box, materials, inserts and pick fees — then check it against a real week of orders.",
      "Ask for a ship-by service level with a stated measurement method, a monthly report, and a credit or termination right when it is missed.",
      "Put the cost of mis-picks and mis-ships on the provider, including the replacement shipment and the return.",
      "Require disclosure of carrier rates and either pass-through at cost or the right to use your own carrier account.",
      "Cap or pre-agree peak surcharges and secure a written capacity commitment for your known high season.",
      "Require FEFO picking on consumer orders and lot capture on the outbound record.",
      "Keep ownership of your order and customer data with a defined export format on exit.",
    ],
    redFlags: [
      "No accuracy standard and no allocation of responsibility for their picking errors.",
      "Carrier rates billed with an undisclosed markup and no right to audit.",
      "Peak surcharges the provider can set unilaterally during your highest-volume months.",
      "A service level with no measurement method and no consequence.",
      "No pack-out standard for glass or cans.",
      "Order and customer data that cannot be exported in a usable format on termination.",
      "Kitting and project work billed hourly with no rate cap and no estimate requirement.",
    ],
    beforeYouSign: [
      "A fully loaded per-order cost for your three most common order shapes, in writing.",
      "Their accuracy and on-time reporting for a comparable client, and the format you will receive.",
      "A pack-out specification you have approved, including materials for glass or cans.",
      "Their peak-season capacity plan and any surcharge schedule.",
    ],
    related: [
      "three-pl-agreement",
      "ecommerce-fulfillment-agreement",
      "pick-and-pack-agreement",
      "warehouse-agreement",
      "shipping-policy",
      "refund-policy",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "transportation-agreement",
    name: "Transportation Agreement",
    category: "logistics",
    subcategory: "Freight and transport",
    aliases: [
      "Freight Agreement",
      "Transportation Services Agreement",
      "Shipping Agreement",
      "Trucking Agreement",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Transportation Provider",
    whenYouNeedIt:
      "When product has to move between your co-packer, your warehouse, a distributor's dock and a retailer's distribution center, and someone other than you is driving it.",
    whatItDoes:
      "Sets the rates, service standards and liability terms for moving your freight. It covers what a lane costs, what extras are chargeable, what condition the product must arrive in, and what happens when a load is late, short, damaged or refused.",
    whyItMatters:
      "Freight is the cost line founders forecast worst, because the quoted lane rate is rarely what the invoice says. Fuel surcharges, detention, layover, liftgate, pallet exchange, reconsignment and lumper charges land afterwards and are hard to dispute without contract language behind you. The bigger exposure is what happens to product in transit: a reefer that ran warm, a load refused at a retailer's dock, or a shortage discovered at receiving becomes a cargo claim, and cargo claims are governed by the contract's liability limits and its notice deadlines.",
    majorTerms: [
      "Lane rates, rate basis and rate validity period",
      "Fuel surcharge mechanism and index reference",
      "Accessorial charges: detention, layover, liftgate, inside delivery, reconsignment",
      "Lumper and unloading charges and who pays them",
      "Temperature requirements and continuous monitoring in transit",
      "Transit time commitments and appointment scheduling",
      "Retailer routing guide compliance and delivery windows",
      "Cargo liability limit and claim procedure",
      "Claim notice deadlines and documentation required",
      "Pallet exchange, pallet return and pallet charges",
      "Insurance requirements and certificates",
      "Term, rate review and termination",
    ],
    clausesToWatch: [
      {
        clause: "Cargo liability limit",
        why: "A limit set by weight or by a fixed amount per shipment can be a small fraction of the value of a full truckload of finished goods.",
      },
      {
        clause: "Claim notice and filing deadlines",
        why: "Short notice windows measured from delivery routinely expire before a shortage or heat-damage issue is discovered at the receiving warehouse.",
      },
      {
        clause: "Temperature commitment and monitoring",
        why: "Without a stated temperature range, a monitoring requirement and a record you can obtain, a load that ran warm is a dispute you cannot evidence.",
      },
      {
        clause: "Accessorial schedule",
        why: "Accessorials that are not listed with rates in the agreement appear on invoices at whatever the provider charges, and detention alone can exceed the line-haul rate.",
      },
      {
        clause: "Retailer delivery window and appointment compliance",
        why: "A late delivery against a retailer's appointment can trigger a retailer penalty charged to you, so the contract needs to say who carries that cost.",
      },
      {
        clause: "Rate validity and increase mechanism",
        why: "Rates quoted subject to change on notice leave your landed cost undefined, which matters most on the lanes you use every week.",
      },
      {
        clause: "Refused and rejected loads",
        why: "If the agreement is silent on who pays for a refused load's return, storage and redelivery, a single rejection at a retail dock becomes an unbudgeted round trip.",
      },
    ],
    cpgIssues: [
      "Temperature-controlled transport for functional beverages, probiotics and chocolate needs a stated range, continuous monitoring and a downloadable record — not a driver's assurance that the unit was running.",
      "Freeze protection matters as much as refrigeration for canned and bottled beverage moving through cold regions in winter, and it is rarely covered unless you ask for it.",
      "Retailer routing guides dictate appointment windows, pallet configuration, labelling and carton markings; non-compliance surfaces as a deduction against your invoice weeks later.",
      "Lumper fees at grocery and club distribution centers are a real, recurring cost that has to be assigned to someone in writing before the first delivery.",
      "Pallet exchange programs and pallet quality standards cause more freight disputes in food and beverage than damage does; agree who supplies, exchanges and pays for pallets.",
      "Case and pallet damage on beverage is high-frequency and low-value per incident, so the claim process needs to be workable at that scale rather than designed for a single catastrophic loss.",
      "Product refused at a retailer's dock for a code-date or temperature reason has to go somewhere, and short-dated stock returned to a warehouse may no longer be saleable at all.",
    ],
    founderMistakes: [
      "Budgeting freight at the quoted lane rate and being consistently over budget once fuel surcharge, detention and lumper fees were added.",
      "Shipping temperature-sensitive product on a dry van in summer because it was cheaper, and losing the load and the retailer's confidence at once.",
      "Discovering a shortage at the receiving warehouse after the claim window had closed, and having no way to recover.",
      "Never reading the retailer's routing guide, then paying delivery-window penalties on a first order that was otherwise perfect.",
      "Assuming cargo coverage matched the value of the truckload, and recovering a fraction of the cost of a destroyed load.",
    ],
    negotiationPoints: [
      "Get the full accessorial schedule with rates attached to the agreement, and a rule that unlisted charges are not payable.",
      "Set a cargo liability limit tied to the value of the goods, or arrange separate cargo coverage and say so in the contract.",
      "Extend claim notice periods and tie them to discovery at the receiving location rather than to delivery.",
      "Specify the temperature range, require continuous monitoring, and make the temperature record available on request as a condition of payment.",
      "Fix lane rates for a stated period with a defined review mechanism rather than accepting rates subject to change.",
      "Assign responsibility for lumper fees, pallet exchange and retailer delivery-window penalties explicitly.",
      "Require certificates of insurance up front and annually thereafter.",
    ],
    redFlags: [
      "Cargo liability limited to a nominal amount per shipment or per pound with no option to declare value.",
      "A claim window so short that a shortage found at receiving cannot be reported in time.",
      "No temperature commitment or monitoring obligation for temperature-sensitive freight.",
      "Accessorial charges referenced but not listed, or listed 'as applicable'.",
      "Rates stated as subject to change at any time.",
      "No allocation of retailer penalties for missed appointments or non-compliant deliveries.",
      "A right to subcontract the load to anyone without notice, vetting or continuing responsibility.",
    ],
    beforeYouSign: [
      "The complete accessorial schedule with rates, attached as an exhibit.",
      "Certificates of insurance showing cargo coverage and its limit.",
      "The routing guide requirements for every retailer you ship to, checked against what this provider can actually do.",
      "A written temperature specification for each product that needs one.",
    ],
    related: [
      "carrier-agreement",
      "freight-broker-agreement",
      "three-pl-agreement",
      "warehouse-agreement",
      "logistics-services-agreement",
      "retail-vendor-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "freight-broker-agreement",
    name: "Freight Broker Agreement",
    category: "logistics",
    subcategory: "Freight and transport",
    aliases: [
      "Broker-Shipper Agreement",
      "Freight Brokerage Agreement",
      "Transportation Broker Agreement",
      "Logistics Broker Agreement",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand (Shipper) + Freight Broker",
    whenYouNeedIt:
      "When you book freight through someone who arranges trucks rather than owning them — which describes most of the people quoting you spot rates by email.",
    whatItDoes:
      "Engages a broker to find and book carriers for your loads. It sets how loads are tendered and priced, what the broker is responsible for in selecting and vetting the carrier, and how cargo claims are handled when the truck that shows up is not the broker's truck.",
    whyItMatters:
      "The single most important fact about a broker is that they usually do not touch your freight. When a load is lost, stolen, heat-damaged or delivered to the wrong dock, the question is whether the broker carries any responsibility or whether your only route is against a carrier you never chose, never met and may not be able to find. Brokers also create a payment exposure that surprises founders: if you pay the broker and the broker does not pay the carrier, the carrier may come looking for the freight charges from you.",
    majorTerms: [
      "Scope: broker arranges transportation and does not itself carry",
      "Load tender, acceptance and rate confirmation process",
      "Rate structure, spot versus contracted lanes, and fuel",
      "Carrier selection and vetting obligations",
      "Required carrier insurance limits and verification",
      "Cargo claim responsibility and the claim process",
      "Broker's liability and any limitation on it",
      "Double-brokering prohibition",
      "Payment terms and protection against a carrier claiming unpaid charges",
      "Temperature and special handling instructions passed to the carrier",
      "Confidentiality of your customer and pricing information",
      "Term and termination",
    ],
    clausesToWatch: [
      {
        clause: "Broker liability for cargo loss",
        why: "Language limiting the broker to 'arranging' transportation with no responsibility for the carrier's performance leaves you chasing a carrier you did not select.",
      },
      {
        clause: "Carrier vetting and insurance verification",
        why: "If the broker has no stated obligation to verify a carrier's authority and insurance before tendering your load, your product can end up on a truck with no meaningful coverage behind it.",
      },
      {
        clause: "Double-brokering",
        why: "A load re-brokered to an unknown third party is the classic setup for cargo theft, and it also destroys the paper trail you need to make a claim.",
      },
      {
        clause: "Payment and carrier claims against the shipper",
        why: "Without a clause addressing it, paying the broker in full does not always end the matter if the broker fails to pay the carrier that hauled your load.",
      },
      {
        clause: "Passing on special handling instructions",
        why: "Temperature ranges, freeze protection and appointment requirements only help if the broker is obliged to put them on the carrier's rate confirmation.",
      },
      {
        clause: "Claim handling and documentation",
        why: "A broker with no obligation to assist, produce the carrier's details or provide the bill of lading can leave you unable to file a claim at all.",
      },
      {
        clause: "Limitation of liability and claim deadlines",
        why: "A low cap combined with a short notice period can extinguish a full-truckload claim before you have finished counting what arrived.",
      },
    ],
    cpgIssues: [
      "Food and beverage loads are a recurring target for cargo theft, and a re-brokered load is the common thread in most of those stories — a clear double-brokering prohibition is worth more than a rate concession.",
      "Temperature instructions get lost between broker and carrier more often than they get ignored by the driver; require the range to appear on the rate confirmation for every temperature-controlled load.",
      "Product recovered after a theft or a temperature excursion cannot simply be put back into distribution, so the loss is usually total even when the pallets come back.",
      "Retailer appointment windows and routing-guide requirements must be passed through to the carrier, because the retailer's penalty lands on your invoice regardless of who missed the slot.",
      "Short-coded product on a delayed load can arrive outside the retailer's minimum remaining shelf life and be refused, converting a service failure into a total loss.",
      "Spot-market rates fluctuate sharply in produce and beverage seasons; a lane you budgeted in winter can price very differently in peak, so treat any quoted rate as time-limited unless the contract fixes it.",
    ],
    founderMistakes: [
      "Assuming the broker was the carrier, and only learning otherwise when a load went missing and there was no one to call.",
      "Booking on price alone through a broker with no vetting obligation, and having a full truckload of finished goods disappear.",
      "Paying the broker in full and then receiving a demand from the carrier that had never been paid.",
      "Giving temperature instructions by phone, with nothing on the rate confirmation, and losing a reefer load with no evidence of what was agreed.",
      "Not requiring the bill of lading and carrier details after delivery, then being unable to document a claim weeks later.",
    ],
    negotiationPoints: [
      "Require the broker to verify carrier operating authority and insurance before every tender, and to provide proof on request.",
      "Prohibit double-brokering outright and make a breach a termination event.",
      "Ask for a clause protecting you from carrier claims for freight charges once you have paid the broker.",
      "Require special handling, temperature and appointment instructions to appear on the carrier rate confirmation.",
      "Negotiate a stated broker responsibility for cargo loss, or confirm in writing what coverage stands behind your freight.",
      "Extend claim notice periods and require the broker to produce the bill of lading and carrier information promptly.",
      "Add confidentiality covering your customer list, lanes and pricing, which a broker sees in full.",
    ],
    redFlags: [
      "No obligation to vet carriers or verify insurance.",
      "Express disclaimer of all responsibility for cargo loss or damage with no alternative recovery route identified.",
      "Silence on double-brokering.",
      "No protection against a carrier pursuing you for charges you already paid the broker.",
      "No obligation to provide carrier identity or the bill of lading after delivery.",
      "A liability cap far below the value of a single truckload of your product.",
    ],
    beforeYouSign: [
      "Written confirmation of what insurance stands behind a lost or damaged load, and whose it is.",
      "Their carrier vetting process, in writing.",
      "The claim process, the documentation required and the deadlines that apply.",
      "Confirmation that temperature and appointment instructions will appear on every rate confirmation.",
    ],
    related: [
      "carrier-agreement",
      "transportation-agreement",
      "three-pl-agreement",
      "logistics-services-agreement",
      "warehouse-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "carrier-agreement",
    name: "Carrier Agreement",
    category: "logistics",
    subcategory: "Freight and transport",
    aliases: [
      "Motor Carrier Agreement",
      "Shipper-Carrier Agreement",
      "Trucking Services Agreement",
      "Dedicated Carrier Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand (Shipper) + Motor Carrier",
    whenYouNeedIt:
      "When you contract directly with the company whose trucks actually move your freight — typically once volume on a lane is steady enough that a dedicated or contracted arrangement beats booking on the spot market.",
    whatItDoes:
      "Creates a direct relationship with the carrier hauling your product: agreed rates and lanes, equipment and temperature standards, service commitments, insurance, and a claim process that runs against the party that had physical custody of the goods.",
    whyItMatters:
      "Contracting directly removes the middle layer and puts your claim against the party that actually had your product, which is the main reason to do it. What you take on in exchange is the responsibility for setting the standards yourself: equipment condition, temperature and monitoring, driver check-in, seal integrity and appointment compliance are all things a broker would otherwise be specifying on your behalf. A direct carrier contract with none of that written down is worse than a good brokered load.",
    majorTerms: [
      "Lanes, committed volume and rate schedule",
      "Fuel surcharge basis and adjustment frequency",
      "Equipment type, condition and pre-cool requirements",
      "Temperature setpoint, continuous monitoring and download on request",
      "Seal application, seal recording and seal integrity on arrival",
      "Transit time and on-time delivery commitment",
      "Detention, layover and accessorial schedule",
      "Cargo liability limit and declared value option",
      "Claim procedure, notice deadlines and documentation",
      "Insurance limits, certificates and additional insured status",
      "Subcontracting and interlining restrictions",
      "Term, rate review and termination",
    ],
    clausesToWatch: [
      {
        clause: "Cargo liability limit",
        why: "A limit expressed per pound or per shipment can leave a full load of finished goods dramatically under-covered relative to its value.",
      },
      {
        clause: "Temperature setpoint and monitoring evidence",
        why: "Without a requirement to record and produce the reefer download, a temperature excursion becomes one party's word against the other's.",
      },
      {
        clause: "Seal integrity",
        why: "If the agreement does not require seals to be applied, recorded on the bill of lading and verified intact at delivery, a shortage claim is very hard to prove.",
      },
      {
        clause: "Subcontracting and interlining",
        why: "A carrier that can hand your load to another carrier without notice gives you the disadvantages of brokered freight while you thought you had a direct relationship.",
      },
      {
        clause: "Detention and accessorials",
        why: "Detention charged from an early free-time threshold at busy retail docks can add up faster than the line-haul rate on a regular lane.",
      },
      {
        clause: "On-time delivery and appointment compliance",
        why: "A service commitment with no consequence does not help when a missed appointment triggers a retailer penalty against your invoice.",
      },
      {
        clause: "Claim deadlines and salvage",
        why: "Short notice periods and a carrier right to sell salvaged product can put damaged goods bearing your label back into the market.",
      },
    ],
    cpgIssues: [
      "Trailer pre-cool before loading matters as much as the setpoint during transit; product loaded into a warm trailer may never recover, and the download will not show it clearly.",
      "Freeze protection on winter lanes is a separate requirement from refrigeration and needs to be specified for canned and bottled beverage.",
      "Salvage rights matter enormously for food and supplements — damaged, out-of-temperature or short-coded product bearing your label should not be resold, and the contract should require destruction with proof.",
      "Retailer routing guides govern appointment windows, pallet patterns, carton labels and unload procedures; the carrier has to be contractually bound to them or the penalties land on you.",
      "Lumper fees at grocery and club distribution centers should be assigned and evidenced, since they recur on every delivery to those accounts.",
      "Pallet quality and exchange requirements differ by retailer, and a rejected pallet at the dock delays the whole appointment and can push the load outside its delivery window.",
      "Loads of finished food and beverage are theft targets, so seal procedure, driver identity verification and stop-off rules belong in the agreement rather than in a phone call.",
    ],
    founderMistakes: [
      "Contracting directly to save the broker's margin, then never specifying equipment, temperature or seal procedure, and losing the first claim for lack of evidence.",
      "Accepting the carrier's standard liability limit and discovering it covered a fraction of a truckload of finished goods.",
      "Not requiring the reefer download, then having a heat-damage claim denied because nothing proved the unit ran warm.",
      "Allowing salvage without restriction, and finding damaged product with their label discounted in a secondary market.",
      "Ignoring detention terms on a lane that delivered into a slow retail dock, and paying detention on most loads for a year.",
    ],
    negotiationPoints: [
      "Specify equipment type, pre-cool requirement, setpoint and continuous monitoring, with the download provided on request as a condition of payment.",
      "Require seals to be applied, recorded on the bill of lading and verified at delivery.",
      "Ask for a declared-value option or a liability limit tied to the value of the goods, plus certificates of insurance.",
      "Prohibit subcontracting or interlining without your prior written consent.",
      "Require destruction with proof for any damaged or out-of-temperature product rather than allowing salvage sale.",
      "Bind the carrier to the routing guides of the retailers you ship to, and assign responsibility for penalties caused by their failures.",
      "Negotiate free time and detention terms against the actual dwell time at the docks you deliver to.",
    ],
    redFlags: [
      "A liability limit set per pound with no declared-value option.",
      "No temperature monitoring or record obligation on refrigerated freight.",
      "An unrestricted right to subcontract or interline the load.",
      "A right to salvage and sell damaged product bearing your brand.",
      "No seal procedure of any kind.",
      "Service commitments with no measurement and no consequence.",
    ],
    beforeYouSign: [
      "Certificates of insurance with cargo limits, and confirmation of whether declared value is available.",
      "The accessorial and detention schedule with free time stated per stop type.",
      "A written equipment and temperature specification for each product that needs one.",
      "The claim process, deadlines and required documentation.",
    ],
    related: [
      "transportation-agreement",
      "freight-broker-agreement",
      "three-pl-agreement",
      "warehouse-agreement",
      "logistics-services-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "logistics-services-agreement",
    name: "Logistics Services Agreement",
    category: "logistics",
    subcategory: "Master and multi-service",
    aliases: [
      "Master Logistics Agreement",
      "Supply Chain Services Agreement",
      "Integrated Logistics Agreement",
      "Logistics MSA",
    ],
    stages: ["distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Logistics Provider (multi-service)",
    whenYouNeedIt:
      "When one provider is doing several things for you — storing, fulfilling, moving freight, managing returns, maybe running more than one site — and you want a single master agreement with service schedules underneath rather than four separate contracts that contradict each other.",
    whatItDoes:
      "Creates an umbrella contract with the commercial and liability terms in one place and each service, site and rate card attached as a schedule that can be added, amended or removed without renegotiating the whole relationship.",
    whyItMatters:
      "Bundling is where providers make their margin and where brands lose their leverage. One agreement covering storage, fulfillment and freight makes it hard to unpick which service is underperforming, harder to price each one against a competitor, and hardest of all to leave the one that is failing without losing the two that work. The value of a master structure is that it lets you terminate a service, a site or a schedule on its own — but only if the termination clause is written at the schedule level and not just at the agreement level.",
    majorTerms: [
      "Master terms with service schedules for each service and site",
      "Rate cards attached per schedule, with validity periods",
      "Key performance indicators per service and the reporting behind them",
      "Governance: review cadence, escalation path and named contacts",
      "Change control for new services, sites, SKUs or volumes",
      "Volume commitments and any minimum charges",
      "Subcontracting rights and the provider's responsibility for subcontractors",
      "Systems, integration and data ownership",
      "Insurance, liability caps and how they apply across services",
      "Termination at agreement level and at schedule level",
      "Transition assistance and exit obligations",
    ],
    clausesToWatch: [
      {
        clause: "Termination at schedule level",
        why: "If you can only terminate the whole agreement, a failing fulfillment operation holds your working warehouse and your freight lanes hostage.",
      },
      {
        clause: "Cross-default and cross-charging between services",
        why: "Language letting the provider suspend one service for a dispute arising in another means a freight invoice argument can stop your orders shipping.",
      },
      {
        clause: "KPIs without consequences",
        why: "Performance targets that produce a report and nothing else give you a monthly conversation instead of a remedy.",
      },
      {
        clause: "Change control",
        why: "Without a defined process and pre-agreed rates, adding a SKU, a site or a service becomes a fresh negotiation from a weak position.",
      },
      {
        clause: "Subcontracting",
        why: "A provider free to subcontract your warehousing or freight can move your inventory into buildings you have never seen and cannot audit.",
      },
      {
        clause: "Transition assistance on exit",
        why: "A master relationship touching every part of your supply chain is the hardest to leave, and without a stated exit obligation you leave in whatever way the provider allows.",
      },
      {
        clause: "Aggregate liability cap",
        why: "A single cap spanning every service can be exhausted by one incident, leaving nothing behind the rest of the relationship.",
      },
    ],
    cpgIssues: [
      "Quality and traceability obligations have to run across every service and site — a lot code captured at one warehouse and lost at another breaks the record exactly where you need it.",
      "Temperature-controlled requirements should sit in the master terms rather than in one site schedule, so adding a second building does not silently drop the commitment.",
      "Retailer compliance — routing guides, labelling, pallet standards, appointment windows — spans warehousing and freight, so responsibility for retailer penalties needs to be allocated once, at master level.",
      "Recall and withdrawal cooperation is a whole-network obligation; the provider must be able to identify and hold affected lots across every site it operates for you.",
      "Multi-site inventory means inventory accuracy has to be defined per site and in aggregate, or a shortfall in one building disappears into the network total.",
      "Certifications a retailer requires of the facilities holding your product should be listed and maintained for the term, per site.",
    ],
    founderMistakes: [
      "Bundling everything with one provider for a better blended rate, then being unable to move the failing piece without losing the parts that worked.",
      "Agreeing KPIs with no service credit or termination trigger, and spending a year in monthly review meetings about the same misses.",
      "Letting the provider add a subcontracted warehouse with no notice, and losing lot-level visibility across the network.",
      "Signing a master agreement with rates only for the first service, then paying whatever was quoted when the second and third were added.",
      "Never negotiating transition assistance, then paying a premium and losing weeks of shipping to exit a provider that touched everything.",
    ],
    negotiationPoints: [
      "Insist on termination rights at the schedule and site level, not only at agreement level.",
      "Attach a rate card to every schedule with a validity period and a defined mechanism for change.",
      "Tie each KPI to a service credit, a cure right and a termination trigger for sustained failure.",
      "Require written consent before any service is subcontracted or any site is added, with the same standards flowing down.",
      "Negotiate transition assistance now: duration, rates, data export, inventory removal and cooperation with the incoming provider.",
      "Keep liability caps per service rather than aggregated across the whole relationship.",
      "Own your data — order, inventory and lot records — with a defined export format.",
    ],
    redFlags: [
      "Termination only at the agreement level, with no way to exit a single failing service.",
      "A right to suspend any service for a dispute arising under any other.",
      "KPIs with no credits, no cure rights and no termination trigger.",
      "Unrestricted subcontracting or site substitution.",
      "One aggregate liability cap across every service.",
      "No transition assistance obligation on exit.",
      "Rates for future services left to be agreed later.",
    ],
    beforeYouSign: [
      "A schedule-by-schedule map of what is included, at what rate, measured how.",
      "The list of sites and subcontractors that will actually hold or handle your product.",
      "A written exit plan covering data, inventory and cooperation with a replacement provider.",
      "Your own comparison of each bundled service against a standalone quote.",
    ],
    related: [
      "three-pl-agreement",
      "warehouse-agreement",
      "fulfillment-agreement",
      "transportation-agreement",
      "carrier-agreement",
      "termination-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "pick-and-pack-agreement",
    name: "Pick-and-Pack Agreement",
    category: "logistics",
    subcategory: "Fulfillment operations",
    aliases: [
      "Pick and Pack Services Agreement",
      "Kitting and Assembly Agreement",
      "Co-Packing Services Agreement (Fulfillment)",
      "Value-Added Services Agreement",
    ],
    stages: ["launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Pick-and-Pack / Kitting Provider",
    whenYouNeedIt:
      "When someone is assembling rather than just shipping: variety packs, club packs, retail-ready displays, sampler boxes, influencer mailers or subscription bundles that have to be built by hand before they can be sold.",
    whatItDoes:
      "Prices and specifies the manual work between your pallets and a saleable unit — picking components, assembling multi-packs, applying labels, building displays and packing to a defined standard — and sets who is responsible when the build is wrong.",
    whyItMatters:
      "Assembly labour is the most under-modelled cost in CPG fulfillment. A variety pack that takes ninety seconds to build by hand can cost more in labour than the margin the pack was designed to earn, and founders usually find out after the club buyer has already agreed a price. The second exposure is that a kitting error is not a shipping error — a display built with the wrong flavour count or a mislabelled multi-pack can be rejected by a retailer as a whole pallet, and the rework charge is billed to you.",
    majorTerms: [
      "Pick fee per unit, per line and per case",
      "Assembly or kitting rate: per kit, per component or hourly",
      "Labelling, relabelling and sticker application charges",
      "Display and retail-ready pack assembly rates",
      "Corrugate, film, dunnage and component supply and cost",
      "Build specification approval and first-article sign-off",
      "Quality check standard and error responsibility",
      "Rework charges and who pays for their errors",
      "Minimum batch size and minimum monthly charge",
      "Lead time from instruction to completion",
      "Component shrinkage and yield loss allowance",
    ],
    clausesToWatch: [
      {
        clause: "Kitting priced hourly with no estimate",
        why: "Open-ended hourly assembly with no quoted per-kit rate means your cost per pack is unknown until the invoice arrives.",
      },
      {
        clause: "Build specification and sign-off",
        why: "Without an approved written spec and a first-article approval, 'built wrong' has no definition and the rework is charged to you.",
      },
      {
        clause: "Component shrinkage and yield loss",
        why: "An unstated allowance for damaged components on an assembly line means you supply more units than you get packs, permanently.",
      },
      {
        clause: "Responsibility for assembly errors",
        why: "If their mistake and your instruction are treated the same way, every rework is billable and there is no incentive to get it right first time.",
      },
      {
        clause: "Minimum batch size",
        why: "A minimum set above a typical promotional run turns a small display order into an expensive one, or forces you to build stock you cannot sell.",
      },
      {
        clause: "Component supply and title",
        why: "If the provider sources corrugate or components, the agreement needs to say who owns unused stock and who pays for it when the promotion ends.",
      },
    ],
    cpgIssues: [
      "Variety and flavour packs require correct component counts by SKU, and a miscount is invisible from the outside of a sealed case until the retailer opens one.",
      "Retail-ready displays and club packs must match the retailer's approved specification exactly — dimensions, facings, header card, pallet pattern — and a non-conforming build gets rejected at the dock.",
      "Lot codes and expiration dates on components must be captured and, ideally, kept consistent within a kit, so a withdrawal does not have to sweep every pack ever built.",
      "Shortest-dated component sets the effective code of the whole multi-pack; build rules should prevent a near-expiry unit going into a pack that then sits on a shelf.",
      "Relabelling for a compliance or artwork change is a project rate, not a fulfillment rate, and repacking an existing run can cost more than the labels themselves.",
      "Assembly of food and supplement product raises handling and sanitation expectations even when nothing is opened; confirm what the site is set up to do before shipping components there.",
    ],
    founderMistakes: [
      "Quoting a club buyer a price on a variety pack without pricing the assembly labour, and shipping the whole program at a loss.",
      "Sending components with no written build spec, then paying for a full rework of several thousand packs built to a verbal description.",
      "Not agreeing a component shrinkage allowance, and being short of units at the end of every run with no explanation.",
      "Building displays that missed the retailer's dimensional spec and having the pallet refused at receiving.",
      "Ordering a relabel project without a per-unit rate, and receiving an hourly invoice larger than the value of the inventory relabelled.",
    ],
    negotiationPoints: [
      "Get a per-kit or per-unit assembly rate rather than an hourly rate, based on a timed sample build.",
      "Require an approved build specification and a first-article sign-off before any full run starts.",
      "Agree a component shrinkage allowance in units, and make loss above it their cost.",
      "Make rework of their errors free, and require a quality check standard with a stated sample rate.",
      "Set lead times from instruction to completion, especially ahead of retailer ship windows.",
      "Agree who owns leftover components and corrugate when a promotion ends.",
    ],
    redFlags: [
      "Assembly billed hourly with no estimate, no cap and no per-unit equivalent.",
      "No written build specification or approval step.",
      "Rework of their own errors billed at full rate.",
      "No stated component shrinkage allowance.",
      "No quality check of any kind before a run ships.",
      "Minimum batch sizes larger than your typical promotional order.",
    ],
    beforeYouSign: [
      "A timed sample build and a per-unit rate derived from it.",
      "A written and approved build specification, with photographs.",
      "The retailer's display or pack specification, checked against what the site can produce.",
    ],
    related: [
      "fulfillment-agreement",
      "three-pl-agreement",
      "ecommerce-fulfillment-agreement",
      "packaging-specification-agreement",
      "warehouse-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "ecommerce-fulfillment-agreement",
    name: "E-Commerce Fulfillment Agreement",
    category: "logistics",
    subcategory: "Fulfillment operations",
    aliases: [
      "DTC Fulfillment Agreement",
      "Online Order Fulfillment Agreement",
      "Direct-to-Consumer Fulfillment Agreement",
      "Subscription Fulfillment Agreement",
    ],
    stages: ["launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + E-Commerce Fulfillment Provider",
    whenYouNeedIt:
      "When your own website, a subscription program or a marketplace channel is shipping parcels to consumers and you want someone else to pick, pack and ship them.",
    whatItDoes:
      "Connects your store to a fulfillment operation: orders flow in automatically, units are picked and packed to your specification, parcels go out under a carrier account, and returns come back to somewhere that can process them.",
    whyItMatters:
      "Direct-to-consumer CPG lives or dies on the cost of shipping one or two units of a heavy, low-priced product, and this is the agreement that sets it. A per-order fee that is reasonable on a large basket is fatal on a single can or a single bottle, and the only way to know which you have is to run your actual order mix through the actual rate card before signing. Founders sign on the headline rate, launch, and discover in month three that the contribution margin they modelled was consumed by an order fee, a carton, a pick fee and dimensional weight.",
    majorTerms: [
      "Per-order fee and what it includes",
      "Pick fees for first and additional units",
      "Packaging, carton, dunnage and branded insert charges",
      "Carrier account, rate pass-through and any markup",
      "Order cutoff time and same-day or next-day ship commitment",
      "Subscription batch processing and recurring order handling",
      "Returns receiving, inspection, restock and disposal",
      "Marketplace and retailer prep services",
      "Storage fees and long-tail SKU handling",
      "Peak surcharge schedule and capacity commitment",
      "Platform integration, order data and reporting",
      "Minimum monthly charge and volume assumptions",
      "Term, termination and inventory removal",
    ],
    clausesToWatch: [
      {
        clause: "Per-order fee against your real basket size",
        why: "An order-level fee is charged whether the customer bought one unit or twelve, so on a single-unit product it behaves like a per-unit cost and can exceed your gross margin.",
      },
      {
        clause: "Carrier rate pass-through and markup",
        why: "Parcel costs are the largest line in direct-to-consumer economics, and an undisclosed markup on them is a permanent, invisible reduction in margin.",
      },
      {
        clause: "Dimensional weight and packaging choice",
        why: "If the provider chooses the box, they have no reason to choose the smallest one, and you pay the dimensional weight on every parcel.",
      },
      {
        clause: "Subscription batch handling",
        why: "Recurring orders released in one batch can trip a same-day commitment or a capacity limit, and a missed subscription cycle produces cancellations rather than late deliveries.",
      },
      {
        clause: "Returns processing charges",
        why: "Per-unit receiving, inspection and disposal fees on a consumable that usually cannot be resold turn every return into two costs with no recovery.",
      },
      {
        clause: "Storage on slow-moving SKUs",
        why: "Long-tail flavours and discontinued variants accumulate storage charges quietly until the storage exceeds the value of the stock.",
      },
      {
        clause: "Data and integration ownership",
        why: "If the integration and the order history live with the provider in a form you cannot export, switching providers means rebuilding and losing your operating history.",
      },
    ],
    cpgIssues: [
      "Beverage and liquid supplements are heavy and cheap relative to their weight, so parcel cost per order is the dominant variable and dimensional pricing punishes bulky multi-packs.",
      "Leakers destroy the whole parcel — pack-out specification for glass, cans and liquids is a cost and quality decision that belongs in the contract, not in an email to the warehouse.",
      "Temperature-sensitive product shipped in summer needs a stated seasonal packaging plan: insulation, cold packs, ship days and hold rules, with someone accountable for applying them.",
      "FEFO picking for consumer orders keeps short-dated units out of the hands of the customers most likely to complain publicly, and it has to be a contractual requirement to happen reliably.",
      "Subscription programs need date-certain releases; a missed cycle on a consumable is a cancellation, not a late parcel, so batch capacity should be committed in writing.",
      "Marketplace prep — labelling, polybagging, carton marking, case quantities, appointment booking — is a separate service with separate failure modes, and a rejected inbound shipment strands inventory outside your control.",
      "Returned food, beverage and supplement units generally cannot go back into saleable stock; agree the disposal process and stop paying to restock what can never be sold again.",
    ],
    founderMistakes: [
      "Modelling fulfillment on an average basket that never existed, and losing money on the single-unit trial orders that made up most of the volume.",
      "Accepting the provider's carrier account without seeing the underlying rates, and paying a markup on every parcel for the life of the contract.",
      "Letting the provider pick the box, then paying dimensional weight on an oversized carton for a two-can order.",
      "Launching a subscription without a committed batch window, and missing a release cycle that produced a wave of cancellations.",
      "Paying restocking fees on returned consumables that were then disposed of anyway.",
      "Building the store integration on the provider's platform with no export, and finding the switching cost higher than the savings from moving.",
    ],
    negotiationPoints: [
      "Run twelve months of real orders through the rate card, including the single-unit orders, before agreeing anything.",
      "Require disclosure of carrier rates, pass-through at cost, or the right to bring your own carrier account.",
      "Approve the packaging specification, including carton sizes, and require the smallest compliant box to be used.",
      "Commit the subscription batch window and capacity in writing, with a remedy if a cycle is missed.",
      "Agree a returns process that skips restocking on units that cannot be resold, and price disposal per unit.",
      "Require FEFO picking on consumer orders and lot capture on the outbound record.",
      "Keep your order, customer and inventory data exportable in a defined format at any time.",
      "Pre-agree peak surcharges and secure a capacity commitment for your known high season.",
    ],
    redFlags: [
      "A rate card quoted only as a blended per-order price with no line-item breakdown.",
      "Carrier rates billed with no disclosure and no audit right.",
      "Peak surcharges set unilaterally with no cap and no notice period.",
      "No pack-out standard for glass, cans or liquids.",
      "No stated ship-by commitment, or one with no remedy attached.",
      "Order and customer data that cannot be exported on termination.",
      "Restocking fees charged on returned consumables that are then disposed of.",
    ],
    beforeYouSign: [
      "Your last twelve months of orders, by units per order, run through their fee schedule line by line.",
      "The carrier rates they will bill you, and confirmation of whether you may use your own account.",
      "An approved pack-out specification, including seasonal packaging for temperature-sensitive product.",
      "Written subscription batch capacity and release timing.",
      "The data export format and the inventory removal terms on exit.",
    ],
    related: [
      "fulfillment-agreement",
      "three-pl-agreement",
      "pick-and-pack-agreement",
      "shipping-policy",
      "subscription-terms",
      "refund-policy",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
];

export default LOGISTICS_DOCS;
